<template>
  <div class="bookmark-list-dialog">
    <list-picker-dialog
      ref="bookmarkSelectDialogRef"
      :items="items"
      :title="t('Bookmarks')"
      :has-help="false"
      :has-actions="false"
    >
      <template #item="{ item }">
        <span class="bookmark-list-dialog__list-item">
          <div
            class="bookmark-list-dialog__list-item-text"
            @click="handleClick(item)"
          >
            {{ item.name }}
          </div>

          <div class="bookmark-list-dialog__list-item-actions">
            <ui-icon
              size="10"
              class="bookmark-list-dialog__delete-button"
              @click="openDeleteDialog(item.index)"
              icon="fas fa-trash-alt"
            ></ui-icon>
          </div>
        </span>
      </template>

      <template #no-items>
        <div class="bookmark-list-dialog__no-entries">
          <div class="bookmark-list-dialog__no-entries-container">
            <div class="bookmark-list-dialog__no-entries-content">
              <div class="bookmark-list-dialog__no-entries-title">
                <template v-if="bookmarks.length">
                  {{ t('No bookmarks matched your search') }}
                </template>
              </div>
              <div v-if="!bookmarks.length" class="bookmark-list-dialog__no-entries-subtitle">
                <div
                  class="text text-left"
                  v-html="
                    t(
                      `Quickly access your saved reports from this section. <br>To bookmark a report, click the arrow icon next to it. <br><a target='_blank' href='{0}'>Learn more</a> in this guide.`,
                      [`https://docs.bemob.com/${locale}/bookmarks`],
                    )
                  "
                ></div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </list-picker-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useBookmarkList, useI18n } from '@/composables';
import ListPickerDialog from '@/components/dialogs/list-picker-dialog.vue';
import type { ListPickerItem } from '@/components/dialogs/list-picker-dialog.types';

type BookmarkPickerItem = ListPickerItem & { index: number };

const { t, locale } = useI18n();
const bookmarkSelectDialogRef = ref<InstanceType<typeof ListPickerDialog> | null>(null);

const { bookmarks, openDeleteDialog, handleOpenBookmark } = useBookmarkList({
  onClose: () => bookmarkSelectDialogRef.value?.close(),
});

const items = computed(() =>
  bookmarks.value.map((item) => ({
    id: item.id,
    name: item.title,
    index: item.index,
  })),
);

const handleClick = (event: BookmarkPickerItem) => {
  const bookmark = bookmarks.value.find((item) => item.id === event.id);
  if (bookmark) {
    handleOpenBookmark(bookmark, true);
  }
};

const open = () => {
  bookmarkSelectDialogRef.value?.open();
};

const close = () => {
  bookmarkSelectDialogRef.value?.close();
};

defineExpose({ open, close });
</script>

<style lang="scss">
.bookmark-list-dialog {
  &__list-item {
    display: flex !important;
    width: 100%;
    align-items: center;
  }

  &__list-item-text {
    flex-grow: 1;
    font-size: 13px;
  }

  &__list-item-actions {
    flex-grow: 0;
  }

  &__no-entries {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__no-entries-container {
    padding: 0 30px;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__no-entries-content {
    flex-grow: 0;
    flex-shrink: 0;
  }

  &__no-entries-title {
    font-size: 24px;
    color: var(--color-graphite);
    text-align: center;
  }

  &__no-entries-subtitle {
    font-size: 18px;
    line-height: 1.33;
    color: var(--color-graphite);
    text-align: center;
    margin-top: 16px;
    margin-bottom: 16px;
  }

  &__delete-button.ui-icon {
    line-height: 30px;

    &:hover {
      color: var(--color-red) !important;
      cursor: pointer;
    }
  }
}
</style>
